import React from 'react';
import useLastFocusedEditorSelection from 'components/text-editor/hooks/use-last-focused-editor-selection';
import useSelectedTextSnippet from 'components/text-editor/components/controls/hooks/use-selected-text-snippet';
import useCustomEditor from 'components/text-editor/hooks/use-custom-editor';
import { useTranslation } from 'i18n';
import { isNull, pick } from 'lodash';
import { AnnotationData } from 'types';
import toast from 'services/toast';

interface Input {
  onTextSnippetSave?(data: AnnotationData): void;
  onTextSnippetDelete?(data: AnnotationData): void;
  onDeleteLink?(data: AnnotationData): void;
  onStartLinking?(data: AnnotationData): void;
  onFinishLinking?(data: AnnotationData): void;
  renderedAt?: string;
}

interface UseMarkupMenuOperations {
  name?: string;
  isNameEditorVisible: boolean;
  isNameDisplayVisible: boolean;
  showNameEditor: Function;
  hideNameEditor: Function;
  saveTextSnippet(name?: string): void;
  deleteTextSnippet(): void;
  startLinking(): void;
  finishLinking(): void;
  deleteLink(): void;
  getSelectedAnnotationData(): AnnotationData;
}

/**
 * When user selects the text:
 *
 * 1. If selection is over existing snippet:
 * - show name editor
 * - If user clicks on other node...deselect
 *
 * 2. If selection is new text
 * - Add custom selection block so that text is highlighted even when user moves focus to name editor form
 * - When user clicks on node that's not the new selected node unwrap the node
 * - If user saves...unwrap the selection node and wrap with custom block
 *
 */

export default function useMarkupMenuOperations(
  props: Input,
): UseMarkupMenuOperations {
  const editor = useCustomEditor();
  const editorSelection = useLastFocusedEditorSelection(editor);

  const { t } = useTranslation();

  const {
    markNodeAsSelected,
    selectedTextSnippet,
    setSelectedTextSnippet,
    clearSelectedTextSnippet,
    updateSelectedTextSnippet,
  } = useSelectedTextSnippet(editor);

  const [shouldShowNameEditor, setNameEditorVisible] = React.useState(false);

  const isNameEditorVisible = Boolean(
    selectedTextSnippet && shouldShowNameEditor,
  );

  const isNameDisplayVisible = Boolean(
    selectedTextSnippet?.name && isNameEditorVisible === false,
  );

  function showNameEditor() {
    // If selected text is different then selected node

    if (!selectedTextSnippet && editorSelection) {
      markNodeAsSelected(editorSelection);
      // @ts-ignore
      const selectedNodes = editor.filterNodes((node) => node.isSelected);

      const hasAnnotations = selectedNodes.some((node) => node.annotationId);

      if (selectedNodes.length > 1 && hasAnnotations) {
        editor.unMarkNodesAsSelected();

        return toast.error(
          t(
            'Cannot create name snippet because text selection contains another annotations. This operation is not currently supported.',
          ),
        );
      }

      setSelectedTextSnippet(editor.getSelectedNode(editorSelection));
    }

    setNameEditorVisible(true);
  }

  function hideNameEditor() {
    setNameEditorVisible(false);
  }

  function saveTextSnippet(name?: string) {
    const node = editor.getSelectedNode();

    if (node && name) {
      const data = getSelectedAnnotationData();

      const savedData = editor.saveAnnotation(editorSelection, {
        ...data,
        name,
        isNamed: true,
        // @ts-ignore
        annotationId: node?.annotationId,
      });

      updateSelectedTextSnippet({ name });

      const snippetData = pick(savedData, [
        'id',
        'name',
        'text',
        'annotationId',
      ]);

      props.onTextSnippetSave && props.onTextSnippetSave(snippetData);

      hideNameEditor();
    }
  }

  function deleteTextSnippet() {
    const selectedNode = editor.getSelectedNode();

    if (selectedNode?.annotationId) {
      editor.deleteAnnotation(
        ['name', 'isNamed', 'isSelected'],
        selectedNode?.annotationId,
      );

      const snippetData = pick(selectedNode, [
        'id',
        'text',
        'name',
        'annotationId',
      ]);

      props.onTextSnippetDelete && props.onTextSnippetDelete(snippetData);
      hideNameEditor();
      clearSelectedTextSnippet();
    }
  }

  async function startLinking() {
    const selectedNode = editor.getSelectedNode();

    if (editorSelection || selectedNode) {
      if (isNull(selectedNode)) {
        markNodeAsSelected(editorSelection);
        setSelectedTextSnippet(editor.getSelectedNode());
      }

      // @ts-ignore
      const selectedNodes = editor.filterNodes((node) => node.isSelected);

      const hasAnnotations = selectedNodes.some((node) => node.annotationId);

      if (selectedNodes.length > 1 && hasAnnotations) {
        editor.unMarkNodesAsSelected();

        return toast.error(
          t(
            'Cannot create name snippet because text selection contains another annotations. This operation is not currently supported.',
          ),
        );
      }

      const data = getSelectedAnnotationData();

      const customData = {
        ...data,
        isLink: true,
        isSelected: false,
        renderedAt: props.renderedAt,
        ...(data.annotationId && { annotationId: data.annotationId }),
      };

      const savedData = editor.saveAnnotation(editor.selection, customData);

      const snippetData = pick(savedData, [
        'id',
        'text',
        'name',
        'annotationId',
      ]);

      clearSelectedTextSnippet();

      props.onStartLinking && props.onStartLinking(snippetData as any);
    }
  }

  async function finishLinking() {
    if (editor.selection) {
      const selectedNode = editor.getSelectedNode();

      if (isNull(selectedNode)) {
        markNodeAsSelected(editor.selection);
      }

      setSelectedTextSnippet(editor.getSelectedNode());

      // @ts-ignore
      const selectedNodes = editor.filterNodes((node) => node.isSelected);

      const hasAnnotations = selectedNodes.some((node) => node.annotationId);

      if (selectedNodes.length > 1 && hasAnnotations) {
        editor.unMarkNodesAsSelected();

        return toast.error(
          t(
            'Cannot create name snippet because text selection contains another annotations. This operation is not currently supported.',
          ),
        );
      }

      const data = getSelectedAnnotationData();

      const selection = editor.selection;

      const savedData = editor.saveAnnotation(selection, {
        ...data,
        isLink: true,
        isSelected: false,
        renderedAt: props.renderedAt,
        ...(data.annotationId && { annotationId: data.annotationId }),
      });

      const snippetData = pick(savedData, [
        'id',
        'text',
        'name',
        'annotationId',
      ]);

      // We are submitting the form so we want to unmark it
      clearSelectedTextSnippet();

      if (props.onFinishLinking) {
        await props.onFinishLinking(snippetData);
        // Update annotation to rerender and show global link indicator
        editor.saveAnnotation(selection, {
          isLink: true,
          updatedAt: new Date().toISOString(),
          annotationId: savedData.annotationId,
        });
      }
    }
  }

  function deleteLink() {
    const data = getSelectedAnnotationData();

    if (data.annotationId) {
      editor.deleteAnnotation(['isLink'], data.annotationId);

      props.onDeleteLink && props.onDeleteLink(data);
    }
  }

  function getSelectedAnnotationData(annotationId?: string): AnnotationData {
    const selectedNode = editor.getSelectedNode();

    return {
      //@ts-ignore
      annotationId: selectedNode?.annotationId ?? annotationId,
      text: selectedNode ? editor.getTextFromNode(selectedNode) : undefined,
      //@ts-ignore
      name: selectedNode?.name as any,
      //@ts-ignore
      isLink: selectedNode?.isLink as any,
    };
  }

  return {
    name: selectedTextSnippet?.name,
    isNameEditorVisible,
    isNameDisplayVisible,
    saveTextSnippet,
    deleteTextSnippet,
    startLinking,
    finishLinking,
    deleteLink,
    showNameEditor,
    hideNameEditor,
    getSelectedAnnotationData,
  };
}
